import HeroWave from "@/components/HeroWave";

/**
 * BlogHero Component Props
 */
interface BlogHeroProps {
  /** Hero title */
  title?: React.ReactNode;
  /** Hero subtitle/description */
  subtitle?: React.ReactNode;
  /** Hero variant */
  variant?: 'default' | 'minimal' | 'centered';
  /** Background image URL (optional) */
  backgroundImage?: string;
}

/**
 * BlogHero Component
 *
 * Displays a hero section for blog pages with title, subtitle, and optional background.
 * Automatically inherits site colors from CSS variables.
 *
 * @param props - Component props
 * @returns BlogHero component
 */
export default function BlogHero({
  title = 'Blog',
  subtitle = 'Thoughts, stories, and ideas',
  variant = 'default',
  backgroundImage
}: BlogHeroProps) {
  if (variant === 'minimal') {
    return (
      <div className="border-b border-border bg-background">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-foreground">{title}</h1>
          {subtitle && (
            <p className="text-muted-foreground mt-2">{subtitle}</p>
          )}
        </div>
      </div>
    );
  }

  if (variant === 'centered') {
    return (
      <div className="relative bg-background border-b border-border">
        {backgroundImage && (
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
        )}
        <div className="relative max-w-4xl mx-auto px-4 py-10 md:py-14 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
            {title}
          </h1>
          {subtitle && (
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div className="relative overflow-hidden bg-background">
      <HeroWave fadeBottom={50} bleed="none" />
      {backgroundImage && (
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
      )}
      <div className="relative max-w-7xl mx-auto px-4 py-10 md:py-14">
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      <div
        className="pointer-events-none absolute inset-x-0 bottom-[-1px] h-28"
        aria-hidden="true"
        style={{
          background:
            'linear-gradient(to bottom, transparent 0%, hsl(var(--background) / 0.72) 62%, hsl(var(--background)) 100%)',
        }}
      />

      {/* Decorative elements */}
      <div className="absolute top-0 right-0 -mt-12 -mr-12 w-64 h-64 bg-primary/10 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-96 h-96 bg-accent/10 rounded-full blur-3xl" aria-hidden="true" />
    </div>
  );
}
